import * as THREE from 'three';
import { useMemo, useEffect } from 'react';

function useCausticsMaterial() {
  // Create a ref to store the material for resize updates
  const materialRef = { current: null };
  
  const material = useMemo(() => new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
      },
      vertexShader: /*glsl*/`
        varying vec2 vUv;
  
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
        fragmentShader: /*glsl*/`
        #ifdef GL_ES
        precision mediump float; // Use medium precision for better performance
        #endif
        
        uniform float uTime;
        uniform vec2 uResolution;

        varying vec2 vUv;

        // Optimized function to create a gradient blob
        float gradientBlob(vec2 uv, vec2 center, float radius, float softness) {
          float dist = length(uv - center);
          return smoothstep(radius + softness, radius - softness, dist);
        }

        // Caustics pattern function
        float causticPattern(vec2 uv, float time) {
          // Create multiple overlapping sine waves at different frequencies and directions
          float caustic = 0.0;
          
          // Layer 1: Basic wave pattern
          caustic += 0.5 * sin(uv.x * 20.0 + uv.y * 15.0 + time * 2.0);
          
          // Layer 2: Crossing pattern at different angle
          caustic += 0.3 * sin(uv.x * 15.0 - uv.y * 25.0 + time * 1.7);
          
          // Layer 3: Finer detail pattern
          caustic += 0.2 * sin(uv.x * 30.0 + uv.y * 30.0 + time * 1.5);
          
          // Layer 4: Very fine detail for caustic "edges"
          caustic += 0.1 * sin(uv.x * 50.0 - uv.y * 40.0 + time * 2.2);
          
          // Normalize and ensure only positive values
          caustic = caustic * 0.5 + 0.5; // Normalize to 0-1 range
          
          // Use smoothstep instead of pow to avoid extreme values
          caustic = smoothstep(0.2, 0.8, caustic);
          
          return caustic;
        }

        void main() {
          vec2 uv = vUv;
          float time = uTime * 0.5;
          
          // Pre-calculate sin/cos values to reuse
          float sinTime1 = sin(time * 0.5);
          float cosTime1 = cos(time * 0.6);
          float cosTime2 = cos(time * 0.7);
          float sinTime2 = sin(time * 0.8);
          
          // Create animated centers for the blobs with faster movement
          vec2 center1 = vec2(
            0.65 + 0.25 * sinTime1,
            0.35 + 0.25 * cosTime1
          );
          
          vec2 center2 = vec2(
            0.25 + 0.25 * cosTime2,
            0.65 + 0.25 * sinTime2
          );
          
          // Reduced from 4 blobs to 2 blobs for better performance
          float blob1 = gradientBlob(uv, center1, 0.25, 0.15);
          float blob2 = gradientBlob(uv, center2, 0.22, 0.15);
          
          // Start with a slightly lighter base
          vec3 baseColor = vec3(0.08, 0.08, 0.15);
          
          // Define deep ocean colors with higher intensity for more contrast
          vec3 color1 = vec3(0.0, 0.3, 0.5) * 0.7;    // Deep blue, increased brightness
          vec3 color2 = vec3(0.0, 0.35, 0.3) * 0.5;   // Teal/aqua with green component
          
          // Generate caustics pattern
          float caustics = causticPattern(uv, time);
          
          // Create a slightly warmer color for the caustics highlights
          vec3 causticsColor = vec3(0.2, 0.5, 0.7);
          
          // Combine colors with additive blending
          vec3 finalColor = baseColor;
          finalColor += color1 * blob1;
          finalColor += color2 * blob2;
          
          // Add caustics effect - only add light, never darkness
          float causticsIntensity = 0.25; // Reduced intensity to prevent overwhelming effect
          
          // Use max to ensure we only add light, never subtract
          float causticsBlend = max(0.0, caustics) * causticsIntensity;
          
          // Make caustics visible everywhere but stronger in blob areas
          float blobInfluence = 0.3 + 0.7 * (blob1 + blob2);
          causticsBlend *= blobInfluence;
          
          // Add the caustics to the final color (additive only)
          finalColor += causticsColor * causticsBlend;
          
          // Output final color with full opacity
          gl_FragColor = vec4(finalColor, 1.0);
        }
      `,
      transparent: false,
      side: THREE.FrontSide,
      depthWrite: false, // Disable depth writing for background
      depthTest: false,  // Disable depth testing for background
    }), []);
    
  // Store the material in the ref
  materialRef.current = material;

  // Update resolution on window resize with debounce
  useEffect(() => {
    // Cache the initial window size to avoid unnecessary updates
    let prevWidth = window.innerWidth;
    let prevHeight = window.innerHeight;
    
    const handleResize = () => {
      // Only update if the size changed significantly (more than 5%)
      const widthChange = Math.abs(window.innerWidth - prevWidth) / prevWidth;
      const heightChange = Math.abs(window.innerHeight - prevHeight) / prevHeight;
      
      if (widthChange > 0.05 || heightChange > 0.05) {
        if (materialRef.current) {
          materialRef.current.uniforms.uResolution.value.set(window.innerWidth, window.innerHeight);
          
          // Update cached dimensions
          prevWidth = window.innerWidth;
          prevHeight = window.innerHeight;
        }
      }
    };
    
    // Initial call to set correct values
    handleResize();
    
    // Add event listener with debounce for better performance
    let resizeTimeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 300);
    };
    
    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  return material;
}

export default useCausticsMaterial;
